import React from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle } from 'react-icons/fa';
import SEO from '../../components/common/SEO';

const IndustryPageTemplate = ({ industryKey, gradient, emoji, featureIcons }) => {
  const { t } = useTranslation();
  const { lang } = useParams();

  return (
    <>
      <SEO 
        titleKey={`industries.${industryKey}.seo.title`}
        descriptionKey={`industries.${industryKey}.seo.description`}
      />
      <PageContainer>
        <HeroSection $gradient={gradient}>
          <HeroContent>
            <HeroTag>{emoji && `${emoji} `}{t(`industries.${industryKey}.hero.tag`)}</HeroTag>
            <HeroTitle>{t(`industries.${industryKey}.hero.title`)}</HeroTitle>
            <HeroSubtitle>{t(`industries.${industryKey}.hero.subtitle`)}</HeroSubtitle>
            <CTAButtons>
              <PrimaryButton to={`/${lang}/demo`} $mainColor={gradient.split(',')[1].trim().split(' ')[0]}>
                {t(`industries.${industryKey}.hero.ctaPrimary`)}
              </PrimaryButton>
              <SecondaryButton to={`/${lang}/contact`}>
                {t(`industries.${industryKey}.hero.ctaSecondary`)}
              </SecondaryButton>
            </CTAButtons>
          </HeroContent>
        </HeroSection>

        <FeaturesSection>
          <SectionTitle>{t(`industries.${industryKey}.features.title`)}</SectionTitle>
          <FeaturesGrid>
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <FeatureCard key={num}>
                <FeatureIcon>{featureIcons ? featureIcons[num] : t(`industries.${industryKey}.features.items.feature${num}.icon`)}</FeatureIcon>
                <FeatureTitle>{t(`industries.${industryKey}.features.items.feature${num}.title`)}</FeatureTitle>
                <FeatureDescription>
                  {t(`industries.${industryKey}.features.items.feature${num}.description`)}
                </FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </FeaturesSection>

        <BenefitsSection>
          <SectionTitle>{t(`industries.${industryKey}.benefits.title`)}</SectionTitle>
          <BenefitsList>
            {Array.from({ length: 14 }, (_, i) => i + 1).map((num) => (
              <BenefitItem key={num}>
                <BenefitIcon $gradient={gradient}><FaCheckCircle /></BenefitIcon>
                <BenefitText>
                  <BenefitTitle>{t(`industries.${industryKey}.benefits.items.benefit${num}.title`)}</BenefitTitle>
                  <BenefitDescription>
                    {t(`industries.${industryKey}.benefits.items.benefit${num}.description`)}
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </BenefitsSection>

        <CTASection $gradient={gradient}>
          <CTAContent>
            <CTATitle>{t(`industries.${industryKey}.cta.title`)}</CTATitle>
            <CTADescription>{t(`industries.${industryKey}.cta.description`)}</CTADescription>
            <CTAButton to={`/${lang}/demo`} $mainColor={gradient.split(',')[1].trim().split(' ')[0]}>
              {t(`industries.${industryKey}.cta.button`)}
            </CTAButton>
          </CTAContent>
        </CTASection>
      </PageContainer>
    </>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: ${props => `linear-gradient(135deg, ${props.$gradient})`};
  padding: 120px 5% 80px;
  color: white;
  text-align: center;

  @media (max-width: 968px) {
    padding: 100px 5% 60px;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1.5rem;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.95;
  margin-bottom: 2.5rem;
  line-height: 1.6;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const PrimaryButton = styled(Link)`
  background: white;
  color: ${props => props.$mainColor};
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.125rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
`;

const SecondaryButton = styled(Link)`
  background: transparent;
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.125rem;
  text-decoration: none;
  border: 2px solid white;
  transition: all 0.3s ease;

  &:hover {
    background: white;
    color: #1a202c;
  }
`;

const FeaturesSection = styled.section`
  padding: 80px 5%;
  background: white;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  text-align: center;
  margin-bottom: 4rem;
  color: #1a202c;

  @media (max-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 2.5rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const FeatureCard = styled.div`
  background: #f8f9fa;
  padding: 2.5rem;
  border-radius: 20px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3.5rem;
  margin-bottom: 1.5rem;
  text-align: center;
  color: ${props => props.theme?.colors?.primary || '#dc2626'};

  svg {
    width: 3.5rem;
    height: 3.5rem;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #1a202c;
`;

const FeatureDescription = styled.p`
  color: #4a5568;
  line-height: 1.6;
  font-size: 1rem;
`;

const BenefitsSection = styled.section`
  padding: 80px 5%;
  background: #f8f9fa;
`;

const BenefitsList = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const BenefitItem = styled.div`
  display: flex;
  gap: 1.5rem;
  padding: 2rem;
  background: white;
  border-radius: 15px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(10px);
    box-shadow: 0 6px 30px rgba(0, 0, 0, 0.12);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const BenefitIcon = styled.div`
  width: 50px;
  height: 50px;
  min-width: 50px;
  background: ${props => `linear-gradient(135deg, ${props.$gradient})`};
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
`;

const BenefitText = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #1a202c;
`;

const BenefitDescription = styled.p`
  color: #4a5568;
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: ${props => `linear-gradient(135deg, ${props.$gradient})`};
  padding: 80px 5%;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  color: white;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2.5rem;
  opacity: 0.95;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: white;
  color: ${props => props.$mainColor};
  padding: 1rem 3rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.125rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.3);
  }
`;

export default IndustryPageTemplate;

